import { useEffect, useRef, useState } from 'react';

interface UseRotatingPlaceholderOptions {
  /** Milliseconds each phrase stays visible before rotating. Default 3200ms — slow enough to read comfortably. */
  intervalMs?: number;
  /** When false, rotation is disabled and the first phrase is shown statically. */
  enabled?: boolean;
}

/**
 * Cycles through a list of placeholder phrases on a slow interval so an input
 * can show a gentle "marquee" of hints (e.g. "Ask Strive anything…",
 * "Type / for skills").
 *
 * Rotation is suppressed — and the first phrase shown statically — when the
 * user prefers reduced motion, when `enabled` is false, or when there is only
 * one phrase. `window.matchMedia` is guarded so the hook is safe in
 * non-browser / jsdom test environments.
 */
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

function readReducedMotion(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(REDUCED_MOTION_QUERY).matches
    : false;
}

export function useRotatingPlaceholder(
  phrases: string[],
  { intervalMs = 3200, enabled = true }: UseRotatingPlaceholderOptions = {},
): string {
  const [index, setIndex] = useState(0);

  // Track the live reduced-motion preference so toggling it mid-session
  // starts/stops rotation, rather than freezing the mount-time value.
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(readReducedMotion);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const mql = window.matchMedia(REDUCED_MOTION_QUERY);
    const onChange = () => setPrefersReducedMotion(mql.matches);
    onChange();
    mql.addEventListener?.('change', onChange);
    return () => mql.removeEventListener?.('change', onChange);
  }, []);

  const shouldRotate = enabled && !prefersReducedMotion && phrases.length > 1;

  // Track the latest length so the interval callback wraps correctly without
  // re-subscribing every time the phrases array identity changes.
  const lengthRef = useRef(phrases.length);
  lengthRef.current = phrases.length;

  useEffect(() => {
    if (!shouldRotate) {
      setIndex(0);
      return;
    }
    const id = window.setInterval(() => {
      // The interval only runs when phrases.length > 1 (see shouldRotate), so
      // the modulo divisor is always >= 2.
      setIndex((i) => (i + 1) % lengthRef.current);
    }, intervalMs);
    return () => window.clearInterval(id);
  }, [shouldRotate, intervalMs]);

  // Clamp in case the phrases list shrank between renders.
  return phrases[index % Math.max(phrases.length, 1)] ?? phrases[0] ?? '';
}
